import { useEffect, useRef, useState } from 'react';

/**
 * Keeps a component mounted through its exit animation. Driven by an
 * `open` boolean: when it flips false, the node stays mounted in the
 * `exit` phase for `durationMs`, then unmounts. Consumers apply an
 * enter animation while `phase === 'enter'` and an exit animation while
 * `phase === 'exit'`.
 *
 * durationMs must match the CSS exit animation duration so the unmount
 * fires exactly as the animation ends — no flash, no premature cut.
 */
export function usePresence(
  open: boolean,
  durationMs: number,
): { mounted: boolean; phase: 'enter' | 'exit' } {
  const [mounted, setMounted] = useState(open);
  const [phase, setPhase] = useState<'enter' | 'exit'>(open ? 'enter' : 'exit');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (open) {
      setMounted(true);
      // Next frame so the enter animation runs from its initial state.
      const raf = requestAnimationFrame(() => setPhase('enter'));
      return () => cancelAnimationFrame(raf);
    }
    setPhase('exit');
    timer.current = setTimeout(() => setMounted(false), durationMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [open, durationMs]);

  return { mounted, phase };
}
